import { atom, useRecoilState } from "recoil";
import { useCallback } from "react";
import { localStorageDB } from "@/Data/LocalStorage";
import {
    getCharacter,
    generateCharacter as generateCharacterAPI,
    getCharacterTask,
    getHomePictureTask,
    UserCharacter,
    CharacterTask,
    HomePictureTask,
} from "@/Data/World";

type CharacterState = {
    // Character data by addressid
    characters: Record<string, UserCharacter>;

    // Active task tracking (taskId stored for polling)
    currentCharacterTaskId: string | null;
    currentCharacterAddressId: string | null;
    characterTask: CharacterTask | null;
    homePictureTask: HomePictureTask | null;

    // Generated home picture URL
    homePicture: string | null;

    // Loading states
    loadingCharacter: boolean;
    loadingHomePicture: boolean;

    // Error states
    error: string | null;
};

const STORAGE_KEY = 'world:character';

const loadFromStorage = (): Partial<CharacterState> => {
    try {
        const stored = localStorageDB.getItem(STORAGE_KEY);
        if (stored) {
            return JSON.parse(stored);
        }
    } catch (e) {
        console.error('Failed to load Character from storage:', e);
    }
    return {};
};

const saveToStorage = (state: CharacterState): void => {
    try {
        localStorageDB.setItem(STORAGE_KEY, JSON.stringify({
            characters: state.characters,
            homePicture: state.homePicture,
        }));
    } catch (e) {
        console.error('Failed to save Character to storage:', e);
    }
};

const defaultState: CharacterState = {
    characters: {},
    currentCharacterTaskId: null,
    currentCharacterAddressId: null,
    characterTask: null,
    homePictureTask: null,
    homePicture: null,
    loadingCharacter: false,
    loadingHomePicture: false,
    error: null,
};

const characterState = atom<CharacterState>({
    key: 'characterState',
    default: { ...defaultState, ...loadFromStorage() },
});

const POLL_INTERVAL = 2000; // 2 seconds

/**
 * Hook for managing character generation and home picture
 */
export const useCharacter = () => {
    const [state, setState] = useRecoilState(characterState);

    const updateState = useCallback((newState: Partial<CharacterState>) => {
        setState(prev => {
            const updated = { ...prev, ...newState };
            saveToStorage(updated);
            return updated;
        });
    }, [setState]);

    const {
        characters,
        currentCharacterTaskId,
        currentCharacterAddressId,
        characterTask,
        homePictureTask,
        homePicture,
        loadingCharacter,
        loadingHomePicture,
        error,
    } = state;

    /**
     * Fetch character for a user
     */
    const fetchCharacter = useCallback(async (addressid: string): Promise<UserCharacter | null> => {
        const data = await getCharacter(addressid);

        if (data) {
            setState(prev => {
                const updated = {
                    ...prev,
                    characters: { ...prev.characters, [addressid]: data },
                };
                saveToStorage(updated);
                return updated;
            });
        }

        return data;
    }, [setState]);

    /**
     * Poll character task until completion
     */
    const pollCharacterTask = useCallback(async (taskid: string): Promise<CharacterTask | null> => {
        const task = await getCharacterTask(taskid);

        if (!task) {
            updateState({ loadingCharacter: false, error: 'Failed to get task status' });
            return null;
        }

        updateState({ characterTask: task });

        if (task.status === 'completed' && task.image) {
            const character: UserCharacter = {
                userid: task.userid,
                image: task.image,
                source_image: task.source_image,
                created_at: task.created_at,
            };

            setState(prev => {
                const updated = {
                    ...prev,
                    characters: { ...prev.characters, [task.userid]: character },
                    loadingCharacter: false,
                    characterTask: null,
                };
                saveToStorage(updated);
                return updated;
            });

            return task;
        }

        if (task.status === 'failed') {
            updateState({ loadingCharacter: false, error: 'Character generation failed' });
            return task;
        }

        // Still processing, poll again
        return new Promise(resolve => {
            setTimeout(async () => {
                const result = await pollCharacterTask(taskid);
                resolve(result);
            }, POLL_INTERVAL);
        });
    }, [setState, updateState]);

    /**
     * Generate character from source image (blocking - waits for completion)
     */
    const generateCharacter = useCallback(async (params?: {
        source_image?: string;
        addressid?: string;
        prompt?: string;
    }): Promise<CharacterTask | null> => {
        updateState({ loadingCharacter: true, error: null });

        const taskid = await generateCharacterAPI(params);

        if (!taskid) {
            updateState({ loadingCharacter: false, error: 'Failed to start character generation' });
            return null;
        }

        return pollCharacterTask(taskid);
    }, [updateState, pollCharacterTask]);

    /**
     * Start character generation (non-blocking)
     * Stores taskId in state and starts internal polling
     */
    const startCharacterGeneration = useCallback(async (params?: {
        source_image?: string;
        addressid?: string;
        prompt?: string;
    }): Promise<string | null> => {
        updateState({
            loadingCharacter: true,
            error: null,
            characterTask: null,
            currentCharacterTaskId: null,
            currentCharacterAddressId: params?.addressid || null,
        });

        const taskid = await generateCharacterAPI(params);

        if (!taskid) {
            updateState({ loadingCharacter: false, error: 'Failed to start character generation' });
            return null;
        }

        // Store taskId in state for polling
        updateState({ currentCharacterTaskId: taskid });

        return taskid;
    }, [updateState]);

    /**
     * Check character task status once (for interval-based polling)
     * Returns the task status, updates state accordingly
     * Uses stored taskId and addressId from state if not provided
     */
    const checkCharacterTask = useCallback(async (taskid?: string, addressid?: string): Promise<CharacterTask | null> => {
        const effectiveTaskId = taskid || currentCharacterTaskId;
        const effectiveAddressId = addressid || currentCharacterAddressId;

        if (!effectiveTaskId) {
            return null;
        }

        const task = await getCharacterTask(effectiveTaskId);

        if (!task) {
            updateState({
                loadingCharacter: false,
                error: 'Failed to get task status',
                currentCharacterTaskId: null,
                currentCharacterAddressId: null,
            });
            return null;
        }

        updateState({ characterTask: task });

        if (task.status === 'completed' && task.image) {
            const character: UserCharacter = {
                userid: task.userid,
                image: task.image,
                source_image: task.source_image,
                created_at: task.created_at,
            };

            // Store by addressid if provided, otherwise by userid
            const key = effectiveAddressId || task.userid;

            setState(prev => {
                const updated = {
                    ...prev,
                    characters: { ...prev.characters, [key]: character },
                    loadingCharacter: false,
                    characterTask: null,
                    currentCharacterTaskId: null,
                    currentCharacterAddressId: null,
                };
                saveToStorage(updated);
                return updated;
            });

            return task;
        }

        if (task.status === 'failed') {
            updateState({
                loadingCharacter: false,
                error: 'Character generation failed',
                characterTask: null,
                currentCharacterTaskId: null,
                currentCharacterAddressId: null,
            });
            return task;
        }

        // Still processing
        return task;
    }, [setState, updateState, currentCharacterTaskId, currentCharacterAddressId]);

    /**
     * Poll home picture task until completion
     */
    const pollHomePictureTask = useCallback(async (taskid: string): Promise<HomePictureTask | null> => {
        const task = await getHomePictureTask(taskid);

        if (!task) {
            updateState({ loadingHomePicture: false, error: 'Failed to get task status' });
            return null;
        }

        updateState({ homePictureTask: task });

        if (task.status === 'completed' && task.image) {
            updateState({
                homePicture: task.image,
                loadingHomePicture: false,
                homePictureTask: null,
            });
            return task;
        }

        if (task.status === 'failed') {
            updateState({ loadingHomePicture: false, error: 'Home picture generation failed' });
            return task;
        }

        // Still processing, poll again
        return new Promise(resolve => {
            setTimeout(async () => {
                const result = await pollHomePictureTask(taskid);
                resolve(result);
            }, POLL_INTERVAL);
        });
    }, [updateState]);

    /**
     * Clear error state
     */
    const clearError = useCallback(() => {
        updateState({ error: null });
    }, [updateState]);

    /**
     * Cancel/reset character generation state
     */
    const cancelCharacterGeneration = useCallback(() => {
        updateState({
            loadingCharacter: false,
            characterTask: null,
            currentCharacterTaskId: null,
            currentCharacterAddressId: null,
            error: null,
        });
    }, [updateState]);

    /**
     * Check if character generation is in progress
     */
    const isGeneratingCharacter = loadingCharacter && currentCharacterTaskId !== null;

    return {
        // State
        characters,
        currentCharacterTaskId,
        characterTask,
        homePictureTask,
        homePicture,
        loadingCharacter,
        loadingHomePicture,
        isGeneratingCharacter,
        error,

        // Actions
        fetchCharacter,
        generateCharacter,
        startCharacterGeneration,
        checkCharacterTask,
        cancelCharacterGeneration,
        clearError,
    };
};
